import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/pt/articles';
import { mainSiteUrl } from '@/mocks/pt/categories';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_URL) || 'https://cssbuyspreadsheet.guru';

const chapterColors = ['bg-accent-100', 'bg-secondary-100'];

const articleImages: Record<string, string> = {
  'guia-cssbuy-spreadsheet': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20desk%20setup%20with%20laptop%20showing%20spreadsheet%20data%20white%20sneakers%20on%20concrete%20floor%20soft%20window%20lighting%20editorial%20aesthetic%20subtle%20shadows%20modern%20workspace&width=1600&height=900&seq=art-hero-guia-2026b&orientation=landscape',
  'como-usar-cssbuy': 'https://readdy.ai/api/search-image?query=Close%20up%20of%20hands%20typing%20on%20laptop%20keyboard%20with%20notebook%20and%20pen%20on%20wooden%20desk%20warm%20golden%20hour%20lighting%20editorial%20aesthetic%20harmonious%20composition%20cozy%20atmosphere&width=1600&height=900&seq=art-hero-como-usar-2026&orientation=landscape',
  'melhores-produtos-cssbuy': 'https://readdy.ai/api/search-image?query=Stylish%20product%20photography%20of%20white%20sneakers%20grey%20hoodie%20and%20sunglasses%20arranged%20on%20marble%20surface%20clean%20minimalist%20composition%20bright%20studio%20lighting%20editorial%20aesthetic%20luxury%20feel&width=1600&height=900&seq=art-hero-melhores-2026&orientation=landscape',
  'cssbuy-vs-superbuy': 'https://readdy.ai/api/search-image?query=Two%20smartphones%20side%20by%20side%20showing%20different%20shopping%20apps%20on%20terracotta%20tile%20surface%20comparison%20concept%20soft%20natural%20lighting%20editorial%20aesthetic%20minimal%20composition&width=1600&height=900&seq=art-hero-vs-2026&orientation=landscape',
  'cssbuy-e-seguro': 'https://readdy.ai/api/search-image?query=Close%20up%20of%20padlock%20and%20credit%20card%20on%20light%20grey%20fabric%20background%20security%20concept%20clean%20minimalist%20product%20photography%20soft%20diffused%20lighting%20editorial%20aesthetic%20trust%20symbolism&width=1600&height=900&seq=art-hero-seguro-2026&orientation=landscape',
  'compras-internacionais-cssbuy': 'https://readdy.ai/api/search-image?query=Elegant%20cardboard%20shipping%20box%20with%20packing%20peanuts%20and%20scissors%20on%20white%20seamless%20background%20clean%20minimalist%20product%20photography%20soft%20studio%20lighting%20editorial%20aesthetic%20ecommerce%20concept&width=1600&height=900&seq=art-hero-compras-2026&orientation=landscape',
  'vendedores-cssbuy': 'https://readdy.ai/api/search-image?query=Professional%20handshake%20silhouette%20with%20product%20boxes%20and%20verification%20checkmarks%20on%20warm%20beige%20wall%20background%20trust%20concept%20soft%20natural%20lighting%20editorial%20aesthetic%20minimalist%20composition&width=1600&height=900&seq=art-hero-vendedores-2026&orientation=landscape',
  'economizar-cssbuy': 'https://readdy.ai/api/search-image?query=Piggy%20bank%20and%20coins%20next%20to%20smartphone%20showing%20shopping%20cart%20on%20wooden%20surface%20saving%20concept%20warm%20natural%20lighting%20editorial%20aesthetic%20clean%20minimalist%20composition&width=1600&height=900&seq=art-hero-economizar-2026&orientation=landscape',
  'tutorial-cssbuy-iniciantes': 'https://readdy.ai/api/search-image?query=Open%20notebook%20with%20handwritten%20notes%20pen%20and%20laptop%20screen%20from%20above%20on%20light%20wood%20desk%20learning%20concept%20warm%20morning%20lighting%20editorial%20aesthetic%20cozy%20composition&width=1600&height=900&seq=art-hero-tutorial-2026&orientation=landscape',
  'cssbuy-portugal': 'https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Lisbon%20colorful%20buildings%20with%20shopping%20bag%20and%20smartphone%20in%20foreground%20Portugal%20concept%20bright%20sunlight%20editorial%20aesthetic%20harmonious%20composition%20vibrant%20colors&width=1600&height=900&seq=art-hero-portugal-2026&orientation=landscape',
};

export default function ArtigoPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex flex-col items-center justify-center min-h-[60vh] px-4 pt-20">
          <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mb-5">
            <i className="ri-error-warning-line text-accent-600 text-2xl w-8 h-8 flex items-center justify-center"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-900 mb-3">Artigo não encontrado</h1>
          <p className="text-foreground-500 mb-6">O artigo que procura não está disponível.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-foreground-900 hover:bg-foreground-800 text-white font-semibold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line text-base w-4 h-4 flex items-center justify-center"></i>
            Voltar ao Início
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const heroImage = articleImages[article.slug] || articleImages['guia-cssbuy-spreadsheet'];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.subtitle,
            author: { '@type': 'Organization', name: 'cssbuyspreadsheet.guru' },
            datePublished: '2026-06-27',
            dateModified: '2026-06-27',
            inLanguage: 'pt',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${siteUrl}/artigo/${article.slug}`,
            },
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
              { '@type': 'ListItem', position: 2, name: article.title, item: `${siteUrl}/artigo/${article.slug}` },
            ],
          }),
        }}
      />

      <main>
        {/* Article Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[480px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/45"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-16 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-xl md:text-3xl lg:text-4xl font-extrabold text-white leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                {article.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-foreground-900 hover:bg-foreground-800 text-white font-semibold text-sm rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line text-base w-4 h-4 flex items-center justify-center"></i>
                  {article.ctaMain}
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-medium text-sm rounded-lg border border-white/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-base w-4 h-4 flex items-center justify-center"></i>
                  {article.ctaBack}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Article Chapters */}
        {article.chapters.map((chapter, chIdx) => (
          <section
            key={chIdx}
            className={`px-4 md:px-6 py-12 md:py-16 ${
              chIdx % 2 === 0 ? 'bg-accent-50/50' : 'bg-background-50'
            }`}
          >
            <div className="max-w-4xl mx-auto">
              <span className="inline-block px-3.5 py-1.5 text-xs font-semibold bg-accent-100 text-accent-800 rounded-full mb-4 whitespace-nowrap">
                {chapter.label}
              </span>

              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                <a id={`capitulo-${chIdx + 1}`} href={`#capitulo-${chIdx + 1}`}>
                  {chapter.title}
                </a>
              </h2>

              <p className="text-sm md:text-base text-foreground-700 mb-5 leading-relaxed">
                <strong>{chapter.intro}</strong>
              </p>

              <p className="text-sm md:text-base text-foreground-600 mb-7 leading-relaxed">
                {chapter.body}
              </p>

              <div className="space-y-3.5 mb-8">
                {chapter.points.map((point, pIdx) => (
                  <div key={pIdx} className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full ${chapterColors[chIdx % chapterColors.length]} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <span className="text-xs font-bold text-foreground-700">{pIdx + 1}</span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed pt-0.5">{point}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {chapter.stats.map((stat, sIdx) => (
                  <div key={sIdx} className="text-center bg-background-50 rounded-lg p-3.5">
                    <div className="text-lg md:text-xl font-extrabold text-foreground-900 mb-1">
                      {stat.value}
                    </div>
                    <p className="text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}

        {/* Back to Home + CTA */}
        <section className="px-4 md:px-6 py-12 bg-background-100">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Continue explorando
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              Volte para a página inicial e descubra mais artigos, categorias e produtos no CSSBuy Spreadsheet.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-foreground-900 hover:bg-foreground-800 text-white font-semibold text-sm rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-4-line text-base w-4 h-4 flex items-center justify-center"></i>
                Voltar ao Início
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 hover:bg-accent-600 text-white font-semibold text-sm rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line text-base w-4 h-4 flex items-center justify-center"></i>
                Acessar CSSBuy Sheet
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}